import { useRef } from 'react';
import { Box, IconButton, Link, Typography } from '@mui/material';
import type { ResponsiveStyleValue } from '@mui/system';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { batch, useDispatch } from 'react-redux';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import type { IProject } from '../../types/global/helper';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER } from '../../pages/BrandDashboard/brandDashboardTokens';
import { setmedia } from '../../redux/project/actions';
import { setMute } from '../../redux/player/action';
import {
    portfolioItemsFromInfluencer,
    publicProfileGridItems,
    type PublicProfileGridItem,
} from './publicInfluencerProfileUtils';

const ON_VARIANT = '#adaaaa';
const sectionPx = { xs: 2, sm: 3, md: 4 };
const GRID_COLUMNS = 4;

function ProfileContentShell({ children, sx }: { children: React.ReactNode; sx?: object }) {
    return <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto', px: sectionPx, ...sx }}>{children}</Box>;
}

function CarouselHeader({ title, onPrev, onNext }: { title: string; onPrev: () => void; onNext: () => void }) {
    return (
        <ProfileContentShell sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: { xs: 26, md: 30 },
                    letterSpacing: '-0.02em',
                }}>
                {title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                    size="small"
                    onClick={onPrev}
                    sx={{
                        color: ON_VARIANT,
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '50%',
                        '&:hover': { borderColor: BRAND_LUXE_PRIMARY, color: BRAND_LUXE_PRIMARY },
                    }}>
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={onNext}
                    sx={{
                        color: ON_VARIANT,
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '50%',
                        '&:hover': { borderColor: BRAND_LUXE_PRIMARY, color: BRAND_LUXE_PRIMARY },
                    }}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        </ProfileContentShell>
    );
}

const gridCellSx = {
    position: 'relative' as const,
    width: '100%',
    aspectRatio: '1 / 1',
    overflow: 'hidden',
    minWidth: 0,
    minHeight: 0,
    bgcolor: '#0a0a0a',
};

const gridImageSx = {
    position: 'absolute' as const,
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
};

function VideoGridCell({
    item,
    onPlay,
}: {
    item: Extract<PublicProfileGridItem, { kind: 'video' }>;
    onPlay: (project: IProject) => void;
}) {
    const canPlay = item.project.processing_complete && item.project.media_type !== 'picture';

    return (
        <Box
            component="button"
            type="button"
            onClick={() => canPlay && onPlay(item.project)}
            disabled={!canPlay}
            aria-label={`Play ${item.project.title}`}
            sx={{
                ...gridCellSx,
                p: 0,
                border: 'none',
                cursor: canPlay ? 'pointer' : 'default',
                opacity: canPlay ? 1 : 0.65,
                display: 'block',
            }}>
            <Box component="img" src={item.thumbnail} alt={item.project.title} loading="lazy" sx={gridImageSx} />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: { xs: 6, sm: 8 },
                    right: { xs: 6, sm: 8 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    lineHeight: 0,
                }}>
                <VideocamOutlinedIcon
                    sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: '#fff',
                        filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.85))',
                    }}
                />
            </Box>
        </Box>
    );
}

function PhotoGridCell({ url }: { url: string }) {
    return (
        <Box sx={{ minWidth: 0, minHeight: 0, width: '100%' }}>
            <PhotoView src={url}>
                <Box sx={{ ...gridCellSx, cursor: 'zoom-in', display: 'block' }}>
                    <Box component="img" src={url} alt="" loading="lazy" sx={gridImageSx} />
                </Box>
            </PhotoView>
        </Box>
    );
}

export interface PublicInfluencerPortfolioSectionsProps {
    influencer: IInfluencer;
    bottomPadding?: ResponsiveStyleValue<number | string>;
}

export function PublicInfluencerPortfolioSections({
    influencer,
    bottomPadding,
}: PublicInfluencerPortfolioSectionsProps) {
    const dispatch = useDispatch();
    const pastRef = useRef<HTMLDivElement>(null);

    const gridItems = publicProfileGridItems(influencer);
    const pastItems = portfolioItemsFromInfluencer(influencer);

    const playProject = (project: IProject) => {
        dispatch(setmedia({ media: project, mediaType: 'act' }));
        batch(() => {
            dispatch(setMute(false));
        });
    };

    const hasContent = gridItems.length > 0 || pastItems.length > 0;
    if (!hasContent) return null;

    const scroll = (ref: React.RefObject<HTMLDivElement | null>, dir: -1 | 1) => {
        ref.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
    };

    const carouselSx = {
        display: 'flex',
        gap: { xs: 2, md: 3 },
        overflowX: 'auto' as const,
        pl: sectionPx,
        pr: sectionPx,
        pb: 2,
        scrollSnapType: 'x mandatory' as const,
        '&::-webkit-scrollbar': { display: 'none' },
    };

    return (
        <Box sx={{ mt: { xs: 6, md: 10 }, pb: bottomPadding }}>
            {gridItems.length > 0 && (
                <ProfileContentShell sx={{ mb: pastItems.length > 0 ? { xs: 6, md: 8 } : 0 }}>
                    <PhotoProvider>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
                                gap: 0,
                                width: '100%',
                            }}>
                            {gridItems.map(item =>
                                item.kind === 'video' ? (
                                    <Box key={item.id} sx={{ minWidth: 0, minHeight: 0, width: '100%' }}>
                                        <VideoGridCell item={item} onPlay={playProject} />
                                    </Box>
                                ) : (
                                    <PhotoGridCell key={item.id} url={item.url} />
                                ),
                            )}
                        </Box>
                    </PhotoProvider>
                </ProfileContentShell>
            )}

            {pastItems.length > 0 && (
                <Box>
                    <CarouselHeader
                        title="Past work"
                        onPrev={() => scroll(pastRef, -1)}
                        onNext={() => scroll(pastRef, 1)}
                    />
                    <Box ref={pastRef} sx={carouselSx}>
                        {pastItems.map(item => (
                            <Box
                                key={item.id}
                                sx={{
                                    minWidth: { xs: 260, md: 300 },
                                    width: { xs: 260, md: 300 },
                                    flexShrink: 0,
                                    scrollSnapAlign: 'start',
                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                    borderRadius: 2,
                                    p: 3,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18 }}>
                                    {item.title}
                                </Typography>
                                {item.subtitle && (
                                    <Typography sx={{ fontSize: 13, color: ON_VARIANT, mt: 0.5 }}>
                                        {item.subtitle}
                                    </Typography>
                                )}
                                {item.description && (
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            color: 'rgba(255,255,255,0.65)',
                                            mt: 1.5,
                                            lineHeight: 1.55,
                                        }}>
                                        {item.description}
                                    </Typography>
                                )}
                                {item.link && (
                                    <Link
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'inline-block',
                                            mt: 2,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            color: BRAND_LUXE_PRIMARY,
                                        }}>
                                        Open link
                                    </Link>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
